import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { DEFAULT_LANDING_SLUG, isSlugVisible, settingsBase } from '@/features/settings/components/nav-config';
import { useSpace, useSpaceAccess } from '@/contexts/space-context';
import { useActiveNavItem } from '@/features/settings/components/useActiveNav';
import { SettingsSidebar } from '@/features/settings/components/SettingsSidebar';
import { PageHeader } from '@/shared/components/PageHeader';
import { ComplianceContent } from '@/features/settings/components/ComplianceContent';
import { MembersContent } from '@/features/settings/components/MembersContent';
import { UsagePage } from './UsagePage';
import { SourceControlSection } from '@/features/settings/sections/SourceControlSection';
import { ConnectorsSection } from '@/features/settings/sections/ConnectorsSection';
import { PlaybooksSection } from '@/features/settings/sections/PlaybooksSection';
import { SkillsSection } from '@/features/settings/sections/SkillsSection';
import { SkillDetailPage } from './SkillDetailPage';
import { TeamGeneralSection } from '@/features/settings/sections/TeamGeneralSection';

/**
 * Settings shell — a standalone (outside the app shell) full-page layout: a left
 * nav rail plus a routed content pane, scoped to the space in the URL
 * (`/app/t/:teamId/settings`). Each section lives in its own file under
 * features/settings/sections; this file only wires routing + layout.
 */
export function SettingsShell() {
  const { teamId } = useSpace();
  const { isPersonal, isAdmin, isResolved } = useSpaceAccess();
  const base = settingsBase(teamId);
  const landing = `${base}/${DEFAULT_LANDING_SLUG}`;

  const current = useActiveNavItem(base);
  const sectionLabel = current?.label ?? 'Settings';
  const subtitle = current?.description ?? '';
  const wide = current?.wide ?? false;

  /** Sections hidden in this space (or for this role) fall back to the landing section. */
  const guard = (slug: string, element: ReactElement) =>
    isSlugVisible(slug, { isPersonal, isAdmin }) ? element : <Navigate to={landing} replace />;

  // Wait for the teams list before routing: the guards need the active team's role.
  const routes = !isResolved ? null : (
    <Routes>
      <Route index element={<Navigate to={landing} replace />} />
      <Route path="team/general" element={guard('team/general', <TeamGeneralSection />)} />
      <Route path="team/members" element={guard('team/members', <MembersContent />)} />
      <Route path="automation/playbooks" element={<PlaybooksSection />} />
      <Route path="automation/skills" element={<SkillsSection />} />
      {/* The editor is a nested route, not a modal — `useActiveNavItem` matches sub-paths, so
          the nav stays highlighted and the section keeps its full-width layout. */}
      <Route path="automation/skills/:skillId" element={<SkillDetailPage />} />
      <Route path="insights/usage" element={guard('insights/usage', <UsagePage />)} />
      <Route path="security/compliance" element={guard('security/compliance', <ComplianceContent />)} />
      <Route path="integrations" element={<Navigate to={`${base}/integrations/source-control`} replace />} />
      <Route
        path="integrations/source-control"
        element={guard('integrations/source-control', <SourceControlSection />)}
      />
      <Route path="integrations/connectors" element={guard('integrations/connectors', <ConnectorsSection />)} />
      {/* Legacy URL — preserve for bookmarks */}
      <Route path="integrations/github" element={<Navigate to={`${base}/integrations/source-control`} replace />} />
      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );

  return (
    <div className="flex h-screen bg-sunken">
      <PageHeader title={`Settings — ${sectionLabel}`} />

      <SettingsSidebar />

      {/* Main pane — a rounded, elevated panel floating on the sunken backdrop */}
      <div className="min-w-0 flex-1 py-3 pr-3">
        <div className="h-full overflow-y-auto rounded-lg border border-line bg-surface-1 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.6)]">
          {wide ? (
            // Full-width sections render their own header (e.g. Members).
            <div key={current?.slug} className="px-12 pb-12 pt-9 animate-fade-in">
              {routes}
            </div>
          ) : (
            <div className="flex justify-center px-12 py-14">
              <div className="flex w-full max-w-180 flex-col gap-10">
                {/* Page header — title + subtitle, scrolls with the content */}
                <div className="flex flex-col">
                  <h1 className="page-title">{sectionLabel}</h1>
                  {subtitle && <p className="page-subtitle">{subtitle}</p>}
                </div>

                {/* Re-key on slug to retrigger the fade-in on navigation */}
                <div key={current?.slug} className="flex flex-col gap-10 animate-fade-in">
                  {routes}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
